import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { POST_COLLECTION_YARD_VISIT_EVENTS_URL } from "../middleware/routes";
import { RootState } from "../store";
import { ICollectionYardVisitEventsState } from "../types/collectionYardVisitEvents";
import { IFilterItem } from "../types/filters";
import { IOrdering } from "../types/pagination";
import { Status } from "../types/state";
import { axiosRequestApi } from "../utils/axiosRequest";
import {
  adaptEventsResponseForFronted,
  adaptEventsSummaryResponseForFronted,
  adaptRequestBodyForBackend,
} from "./utils/collectionYardVisitEventsAdapt";
import { filterOutUndefinedFilters } from "./utils/filters";

const initialState: ICollectionYardVisitEventsState = {
  list: [],
  summary: null,
  locationId: 0,
  visitId: 0,
  status: Status.success,
  errorMsg: null,
  scrollTop: 0,
  pagination: {
    page: 1,
    limit: 50,
  },
  ordering: { orderBy: "wasteTypeId", descending: false },
  filters: { items: [], linkOperator: "and" },
  isNextPageEnable: false,
};

const collectionYardVisitEventsSlice = createSlice({
  name: "collectionYardVisitEvents",
  initialState,
  reducers: {
    setCollectionYardVisitEventsPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination = action.payload;
    },
    setCollectionYardVisitEventsScrollTop: (state, action: PayloadAction<number>) => {
      state.scrollTop = action.payload;
    },
    setCollectionYardVisitEventsVisitId: (state, action: PayloadAction<number>) => {
      state.visitId = action.payload;
    },
    setCollectionYardVisitEventsOrdering: (state, action: PayloadAction<IOrdering>) => {
      state.ordering = action.payload;
    },
    setCollectionYardVisitEventsFilter: (state, action: PayloadAction<IFilterItem>) => {
      const idx = state.filters.items.findIndex((item) => item.id === action.payload.id);

      if (idx === -1) {
        state.filters.items.push(action.payload);
      } else {
        state.filters.items[idx] = action.payload;
      }
      state.filters.items = filterOutUndefinedFilters(state.filters.items, action.payload);
    },
    removeCollectionYardVisitEventsFilter: (state, action: PayloadAction<string>) => {
      state.filters.items = state.filters.items.filter((item) => !item.id?.toString().startsWith(action.payload));
    },
    resetCollectionYardVisitEventsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCollectionYardVisitEventsThunk.pending, (state) => {
        state.status = Status.requesting;
      })
      .addCase(getCollectionYardVisitEventsThunk.fulfilled, (state, action) => {
        state.status = Status.success;
        state.list = action.payload;
        state.isNextPageEnable = action.payload.length >= state.pagination.limit;
      })
      .addCase(getCollectionYardVisitEventsThunk.rejected, (state) => {
        state.status = Status.error;
        state.errorMsg = "Error fetching collection yard visit events";
      })
      .addCase(getCollectionYardVisitEventsSummaryThunk.pending, (state) => {
        state.status = Status.requesting;
      })
      .addCase(getCollectionYardVisitEventsSummaryThunk.fulfilled, (state, action) => {
        state.status = Status.success;
        state.summary = action.payload;
      })
      .addCase(getCollectionYardVisitEventsSummaryThunk.rejected, (state) => {
        state.status = Status.error;
        state.errorMsg = "Error fetching visit events summary";
      });
  },
});

export const getCollectionYardVisitEventsThunk = createAsyncThunk<any, number, { state: RootState }>(
  "collectionYards/getCollectionYardVisitEventsThunk",
  async (visitId, { getState }) => {
    const state = getState().collectionYardVisitEvents;

    const requestBody = adaptRequestBodyForBackend(state);
    const response = await axiosRequestApi.post(
      `${POST_COLLECTION_YARD_VISIT_EVENTS_URL}/${visitId}/events`,
      requestBody
    );
    return adaptEventsResponseForFronted(response.data);
  }
);

export const getCollectionYardVisitEventsSummaryThunk = createAsyncThunk<any, number, { state: RootState }>(
  "collectionYards/getCollectionYardVisitEventsSummaryThunk",
  async (visitId) => {
    const response = await axiosRequestApi.get(`${POST_COLLECTION_YARD_VISIT_EVENTS_URL}/${visitId}/summary`);
    return adaptEventsSummaryResponseForFronted(response.data);
  }
);

export const {
  setCollectionYardVisitEventsPagination,
  setCollectionYardVisitEventsScrollTop,
  setCollectionYardVisitEventsVisitId,
  setCollectionYardVisitEventsOrdering,
  setCollectionYardVisitEventsFilter,
  removeCollectionYardVisitEventsFilter,
  resetCollectionYardVisitEventsState,
} = collectionYardVisitEventsSlice.actions;

export const collectionYardVisitEventsReducer = collectionYardVisitEventsSlice.reducer;
